/**
 * Admin permissions
 *
 * Per-admin capability configuration controlled by super-admins. Designed to
 * grow: it is a single object persisted as a JSON string on `admins.permissions`
 * (see AdminRepository), so new permission categories become new keys with no
 * schema migration.
 *
 * The first category is **game access** — whether an admin may create/manage all
 * game types or only a selected subset. A missing/empty blob normalizes to full
 * access, keeping pre-existing admins (and super-admins) unrestricted.
 */
import { GameType } from '../constants/gameTypes';
/** Default: unrestricted access to all game types. */
export const DEFAULT_ADMIN_PERMISSIONS = {
    gameAccess: { mode: 'all', gameModes: [] },
};
/** All valid game-type values, for validating persisted/user-supplied data. */
const VALID_GAME_TYPES = new Set(Object.values(GameType));
/**
 * Coerce arbitrary stored/submitted data into a well-formed AdminPermissions.
 *
 * Accepts an object or a JSON string (or null/undefined). Anything missing or
 * malformed falls back to full access, so an empty `{}` column (the DB default)
 * and legacy rows both mean "all games". Invalid `gameModes` entries are filtered
 * out and duplicates removed; when `select` yields no valid modes, we downgrade
 * to `all` rather than silently locking the admin out of every game.
 */
export function normalizeAdminPermissions(raw) {
    let value = raw;
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        }
        catch {
            return { gameAccess: { mode: 'all', gameModes: [] } };
        }
    }
    if (!value || typeof value !== 'object') {
        return { gameAccess: { mode: 'all', gameModes: [] } };
    }
    const gameAccessRaw = value.gameAccess;
    if (!gameAccessRaw || typeof gameAccessRaw !== 'object') {
        return { gameAccess: { mode: 'all', gameModes: [] } };
    }
    const mode = gameAccessRaw.mode === 'select' ? 'select' : 'all';
    if (mode === 'all') {
        return { gameAccess: { mode: 'all', gameModes: [] } };
    }
    const modesRaw = gameAccessRaw.gameModes;
    const gameModes = Array.isArray(modesRaw)
        ? Array.from(new Set(modesRaw.filter((m) => typeof m === 'string' && VALID_GAME_TYPES.has(m))))
        : [];
    // A 'select' with no valid modes is meaningless (and dangerous) — treat as all.
    if (gameModes.length === 0) {
        return { gameAccess: { mode: 'all', gameModes: [] } };
    }
    return { gameAccess: { mode: 'select', gameModes } };
}
/**
 * Whether the given permissions allow creating/managing a specific game type.
 * Single source of truth for the server-side gate and any client-side filtering.
 */
export function canCreateGameMode(permissions, mode) {
    const { gameAccess } = permissions;
    return gameAccess.mode === 'all' || gameAccess.gameModes.includes(mode);
}
